import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeading,
  LegendList,
  LegendListItem,
  Link,
  Typography,
} from 'omakase';
import { ConfirmationHeading } from 'components/business/receipts/ConfirmationHeading';
import { DisplayDate } from '@tock/time';
import { IconEnvelope, IconPhone } from '@tock/icons';
import { Instant } from 'js-joda';
import { answerContainer, contentHeader } from './answeredQuestionsDialogCssDefs';
import { core } from '@tock/coretypes';
import {
  getFormattedResponseFromQuestion,
  getOrderedPurchaseQuestions,
} from '@tock/shared/utils/questionUtils';

type AnsweredQuestionsDialogProps = {
  purchase: core.PastPurchase;
  open: boolean;
  onClose(): void;
};

export const AnsweredQuestionsDialog = ({
  open,
  onClose,
  purchase,
}: AnsweredQuestionsDialogProps) => {
  const { business, question } = purchase;
  const lastUpdated = question?.[0]?.lastUpdatedAtTimestampMs;
  const orderedQuestions = getOrderedPurchaseQuestions(question);

  return (
    <Dialog open={open} aria-labelledby="answers-title" data-testid="answers-dialog">
      <DialogHeading id="answers-title" onClose={onClose}>
        Pre-visit questions
      </DialogHeading>
      <DialogContent>
        <header css={contentHeader}>
          <ConfirmationHeading variant="body02Strong">Answers submitted</ConfirmationHeading>
          {lastUpdated && (
            <Typography color="fgSubtle" variant="caption">
              <DisplayDate
                dateValue={Instant.ofEpochMilli(lastUpdated)}
                showWeekday
                displayMonthAs="short"
                displayWeekdayAs="short"
              />
            </Typography>
          )}
        </header>

        <div css={answerContainer}>
          {orderedQuestions.map((q) => {
            const { response, comment } = getFormattedResponseFromQuestion(q);
            return (
              <div key={q.id}>
                <Typography variant="caption" color="fgSubtle" mb={0.5} data-testid="question">
                  {q.text}
                </Typography>
                {response ? (
                  <Typography variant="body02Strong" data-testid="response">
                    {response}
                  </Typography>
                ) : (
                  <Typography variant="body02" color="fgSubtle" data-testid="response">
                    no response
                  </Typography>
                )}

                {comment && (
                  <Typography variant="body02Strong" data-testid="comment">
                    {comment}
                  </Typography>
                )}
              </div>
            );
          })}
        </div>
        {business && (
          <>
            <Typography color="fgSubtle" variant="body02" mb={2}>
              If you need to edit or update your answers, please contact {business.name}.
            </Typography>

            <LegendList gap={1}>
              {business.phone && (
                <LegendListItem icon={<IconPhone />}>
                  <Link href={`tel:${business.phone}`}>{business.phone}</Link>
                </LegendListItem>
              )}
              {business.email && (
                <LegendListItem icon={<IconEnvelope />}>
                  <Link href={`mailto:${business.email}`}>{business.email}</Link>
                </LegendListItem>
              )}
            </LegendList>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button size="medium" variant="primary" onClick={onClose} fullWidth>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};
